// Generic

import React, { useEffect, useState } from 'react'

import { DatePicker } from 'antd'
import Content from 'components/content'
import Select from 'antd/es/select'
import moment from 'moment'

import './style.scss'
import Button from 'components/button'
import { EmployeeReport, EmployeeReportStatusExtended, reportStatuses } from 'models'
import { useEmployeeReporting } from 'context/employee-reporting'
import { useEmployees } from 'context/employees'
import { useCustomers } from 'context/customers'
import { CreateReportModal } from './create-report-modal'
import { millisecondsToDuration } from 'functions/helpers'
import Checkbox from 'antd/es/checkbox'
import Table, { Tr, Td, Th } from 'components/table'
import { ChangeReportStatusModal } from './change-report-status-modal'
import { DeleteReportModal } from './delete-report-modal'
import { useCurrentUser } from 'context/current-user'
import Notification from 'antd/es/notification'
import { downloadExcel } from './download-excel'
import { ReportStatusIndicator } from './status-indicator'
import { Dropdown } from 'antd'
import { Calendar } from 'primereact/calendar'
import './style.scss'
import { isArray } from 'lodash'

const { RangePicker } = DatePicker

type VisibleModal = 'create-report' | 'delete-multiple' | 'change-multiple-status' | undefined

const EmployeeHourReport = () => {
  const {
    data: employeeReports,
    loading: loadingEmployeeReports,
    queryParams,
    setQueryParam,
    createEmployeeReport,
    refetch: refetchEmployeeReporting,
    updateEmployeeReport,
    deleteEmployeeReport,
    deleteMultipleEmployeeReports,
    changeMultipleEmployeeReportsStatus
  } = useEmployeeReporting()

  const { data: employees, loading: loadingEmployees, refetch: refetchEmployees, setQueryParamValues: employeesSetQueryParamValues } = useEmployees()
  const { currentUser, currentUserIsAdmin } = useCurrentUser()

  const { data: customers, loading: loadingCustomers, refetch: refetchCustomers } = useCustomers()

  const [visibleModal, setVisibleModal] = useState<VisibleModal>(undefined)

  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([])

  const [editableEmployeeReport, _setEditableEmployeeReport] = useState<EmployeeReport | undefined>()

  const setEditableEmployeeReport = (item: EmployeeReport | undefined) => {
    if (item) {
      _setEditableEmployeeReport(item)
      setVisibleModal('create-report')
    } else {
      _setEditableEmployeeReport(undefined)
      setVisibleModal(undefined)
    }
  }

  const loading = loadingEmployeeReports || loadingCustomers || loadingEmployees

  const toggleAllItems = () => {
    let selectedIds = [...selectedReportIds]
    selectedIds = []
    if (selectedReportIds.length !== employeeReports?.data?.length) {
      employeeReports?.data.map((item) => {
        selectedIds.push(item._id)
      })
    }

    setSelectedReportIds(selectedIds)
  }

  const toggleSelectedReportIds = (employeeReport: EmployeeReport) => {
    let cloned = [...selectedReportIds]

    if (currentUserIsAdmin || employeeReport.status === 'open') {
      if (cloned.includes(employeeReport._id)) {
        cloned = cloned.filter((item) => item !== employeeReport._id)
      } else {
        cloned.push(employeeReport._id)
      }

      setSelectedReportIds(cloned)
    } else {
      Notification.info({ message: 'Voit muokata vain avoimia raportteja', placement: 'bottomLeft', duration: 2 })
    }
  }

  const dateFormat = 'dddd DD.MM'

  const selectStatus = (item: EmployeeReportStatusExtended) => {
    let current = queryParams.status ? JSON.parse(JSON.stringify(queryParams.status)) : []

    const selectAll = () => {
      current = ['closed', 'waiting_for_approval', 'open', 'invoicing']
    }

    if (item === 'all') {
      selectAll()
    } else {
      if (current.includes(item)) {
        if (current.length > 0) {
          current = current.filter((i: any) => i !== item)
        }
      } else {
        current.push(item)
      }
    }

    if (current.length === 4) {
      selectAll()
    }

    setQueryParam([{ key: 'status', value: current }])
  }

  useEffect(() => {
    employeesSetQueryParamValues([{ key: 'roles', value: ['Employee'] }])
    refetchCustomers()
    refetchEmployees()
    refetchEmployeeReporting()
  }, [])

  useEffect(() => {
    setSelectedReportIds([])
  }, [queryParams])

  const [mobileDatePickerValue, setMobileDatepickerValue] = useState<Date | Date[] | undefined>(undefined)

  useEffect(() => {
    if (mobileDatePickerValue && isArray(mobileDatePickerValue)) {
      if (mobileDatePickerValue[0] && mobileDatePickerValue[1]) {
        setQueryParam([
          { key: 'start_time_iso_date', value: moment(mobileDatePickerValue[0]).startOf('day') },
          { key: 'end_time_iso_date', value: moment(mobileDatePickerValue[1]).endOf('day') }
        ])
      } else {
        setQueryParam([
          { key: 'start_time_iso_date', value: moment(mobileDatePickerValue[0]).startOf('day') },
          { key: 'end_time_iso_date', value: moment(mobileDatePickerValue[0]).endOf('day') }
        ])
      }
    } else {
      setQueryParam([
        { key: 'start_time_iso_date', value: undefined },
        { key: 'end_time_iso_date', value: undefined }
      ])
    }
  }, [mobileDatePickerValue])

  useEffect(() => {
    if (loading) {
      return
    }

    const updates: { key: 'employee_id' | 'customer_id' | 'assignment_id'; value: undefined }[] = []

    if (queryParams.employee_id && !employees?.some((employee) => employee._id === queryParams.employee_id)) {
      updates.push({ key: 'employee_id', value: undefined })
    }

    if (queryParams.customer_id && !customers?.some((customer) => customer._id === queryParams.customer_id)) {
      updates.push({ key: 'customer_id', value: undefined })
      updates.push({ key: 'assignment_id', value: undefined })
    } else if (queryParams.assignment_id) {
      const selectedCustomer = customers?.find((customer) => customer._id === queryParams.customer_id)
      const assignmentExists = selectedCustomer
        ? selectedCustomer.assignments.some((assignment) => assignment._id === queryParams.assignment_id)
        : customers?.some((customer) => customer.assignments.some((assignment) => assignment._id === queryParams.assignment_id))

      if (!assignmentExists) {
        updates.push({ key: 'assignment_id', value: undefined })
      }
    }

    if (updates.length > 0) {
      setQueryParam(updates)
    }
  }, [
    loading,
    employees,
    customers,
    queryParams.employee_id,
    queryParams.customer_id,
    queryParams.assignment_id,
    setQueryParam
  ])

  return (
    <>
      {/* Create Report Modal */}
      <CreateReportModal
        currentUserId={currentUser?._id}
        visible={visibleModal === 'create-report'}
        onClose={() => {
          setEditableEmployeeReport(undefined)
          setVisibleModal(undefined)
        }}
        report={editableEmployeeReport}
        customers={customers}
        isAdmin={currentUserIsAdmin}
        employees={employees}
        onCreate={async (employeeReport) => {
          try {
            await createEmployeeReport(employeeReport)
            setVisibleModal(undefined)
          } catch (e) {}
        }}
        onDelete={async (employeeReport) => {
          try {
            await deleteEmployeeReport(employeeReport)
            setVisibleModal(undefined)
            setSelectedReportIds([])
          } catch (e) {}
        }}
        onUpdate={async (employeeReport) => {
          try {
            await updateEmployeeReport(employeeReport)
            setVisibleModal(undefined)
          } catch (e) {}
        }}
      />
      {/* Delete  */}
      <ChangeReportStatusModal
        visible={visibleModal === 'change-multiple-status'}
        selectedReportIds={selectedReportIds}
        isAdmin={currentUserIsAdmin}
        onClose={() => {
          setVisibleModal(undefined)
        }}
        onSave={async (params) => {
          try {
            await changeMultipleEmployeeReportsStatus(params)
            setVisibleModal(undefined)
            setSelectedReportIds([])
          } catch (e) {}
        }}
      />
      {/* Delete  */}
      <DeleteReportModal
        visible={visibleModal === 'delete-multiple'}
        selectedReportIds={selectedReportIds}
        onClose={() => {
          setVisibleModal(undefined)
        }}
        onDelete={async (params) => {
          try {
            await deleteMultipleEmployeeReports(params)
            setVisibleModal(undefined)
            setSelectedReportIds([])
          } catch (e) {}
        }}
      />
      {/* Content */}
      <Content
        contentScroll={true}
        contentDisablePadding={true}
        toolbarLeft={
          <>
            <h1 className="s-hide-mobile" style={{ minWidth: '100px' }}>
              Tuntikirjaus ({employeeReports?.data.length})
            </h1>
            <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
              {currentUserIsAdmin && (
                <>
                  {/* Asiakas */}
                  <Select
                    placeholder={'Asiakas'}
                    value={!loading && (queryParams.customer_id as unknown as any)}
                    loading={loading}
                    style={{ marginLeft: '10em', width: '200px', marginRight: '1em' }}
                    onChange={(value) => {
                      if (value === 'all') {
                        setQueryParam([
                          { key: 'customer_id', value: undefined },
                          { key: 'assignment_id', value: undefined }
                        ])
                      } else {
                        setQueryParam([
                          { key: 'customer_id', value },
                          { key: 'assignment_id', value: undefined }
                        ])
                      }
                    }}
                  >
                    <Select.Option value={'all'}> </Select.Option>
                    {customers?.map((item) => {
                      return (
                        <Select.Option key={item._id} value={item._id}>
                          {item.name}
                        </Select.Option>
                      )
                    })}
                  </Select>
                  {/* Toimeksianto */}
                  <Select
                    placeholder={'Toimeksianto'}
                    value={!loading && (queryParams.assignment_id as unknown as any)}
                    loading={loading}
                    disabled={!queryParams.customer_id}
                    style={{ width: '200px', marginRight: '1em' }}
                    onChange={(value) => {
                      if (value === 'all') {
                        setQueryParam([{ key: 'assignment_id', value: undefined }])
                      } else {
                        setQueryParam([{ key: 'assignment_id', value }])
                      }
                    }}
                  >
                    <Select.Option value={'all'}> </Select.Option>
                    {customers
                      ?.find((c) => {
                        return JSON.stringify(c._id) === JSON.stringify(queryParams.customer_id)
                      })
                      ?.assignments.map((item) => {
                        return (
                          <Select.Option key={item._id} value={item._id}>
                            {item.name}
                          </Select.Option>
                        )
                      })}
                  </Select>
                  {/* Työntekijä */}
                  <Select
                    style={{ width: '200px' }}
                    value={!loading && (queryParams.employee_id as unknown as any)}
                    loading={loading}
                    placeholder={'Työntekijä'}
                    onChange={(value) => {
                      if (value === 'all') {
                        setQueryParam([{ key: 'employee_id', value: undefined }])
                      } else {
                        setQueryParam([{ key: 'employee_id', value }])
                      }
                    }}
                  >
                    {/* Status */}
                    <Select.Option value={'all'}> </Select.Option>
                    {employees?.map((item) => {
                      return (
                        <Select.Option key={item._id} value={item._id}>
                          {item.last_name} {item.first_name}
                        </Select.Option>
                      )
                    })}
                  </Select>
                </>
              )}
              <div style={{ width: '250px', marginRight: '1em', marginLeft: '1em' }}>
                {/* Päivämäärät */}

                <Calendar
                  style={{ width: '100%' }}
                  // className="s-hide-desktop"
                  dateFormat="dd.MM yy"
                  id="range"
                  value={mobileDatePickerValue}
                  placeholder={'Valitse aikaväli'}
                  showButtonBar
                  onChange={(e) => {
                    setMobileDatepickerValue(e.value)
                  }}
                  selectionMode="range"
                />
              </div>
            </div>
            {/* Status Selector Desktop */}
            <div className="s-show-mobile">
              <Dropdown
                overlay={
                  <div style={{ background: '#253752', padding: '1em' }}>
                    {reportStatuses.map((item, index) => {
                      return (
                        <Button
                          label={item.value}
                          style={{ marginBottom: '2px', width: '100%', color: 'white' }}
                          color={queryParams.status?.includes(item.key) ? 'blue' : 'default'}
                          onClick={() => {
                            selectStatus(item.key)
                          }}
                        />
                      )
                    })}
                  </div>
                }
              >
                <div
                  style={{
                    color: queryParams?.status && queryParams?.status?.length > 0 ? 'rgba(31, 126, 194, 1)' : 'rgba(5,0,0,0.1)',
                    fontSize: '20px',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fa-solid fa-filter"></i>
                </div>
              </Dropdown>
            </div>
            {/* Status Selector Mobile */}

            <div className="s-hide-mobile" style={{ display: 'flex' }}>
              {reportStatuses.map((item, index) => {
                return (
                  <Button
                    label={item.value}
                    style={{ marginLeft: '2px' }}
                    color={queryParams.status?.includes(item.key) ? 'blue' : 'default'}
                    onClick={() => {
                      selectStatus(item.key)
                    }}
                  />
                )
              })}
            </div>
          </>
        }
        fullAbsolute={true}
        toolbarTheme={'white-shadow'}
        loading={loading && 'Tuntikirjaus'}
        toolbarLeftStyle={{ minWidth: '350px;' }}
        loaderTheme={'transparent'}
      >
        {(!employeeReports || employeeReports?.data?.length === 0) && !loading && (
          <div className="c-employee-reports__empty-state">
            <div>
              <div>
                <i className="fa-solid fa-circle-exclamation"></i>
              </div>
              <div>Raportteja ei löytynyt</div>
            </div>
          </div>
        )}
        {employeeReports && employeeReports?.data?.length > 0 && (
          <Table
            thead={
              <Tr>
                <Th _id="select" style={{ width: '55px' }}>
                  <Checkbox onClick={ () => toggleAllItems()} checked={selectedReportIds.length === employeeReports?.data?.length }/>
                </Th>
                <Th _id="id" style={{ width: '80px' }}>
                  ID
                </Th>
                <Th _id="city" style={{ width: '270px' }}>
                  Päivämäärä
                </Th>
                <Th _id="name" style={{ width: '200px' }}>
                  {global.Translations.get('customer')}
                </Th>

                <Th _id="contacts" style={{ width: '200px' }}>
                  Toimeksianto
                </Th>
                <Th _id="contacts" style={{ width: '200px' }}>
                  Työntekijä
                </Th>
                <Th _id="contacts" style={{ width: '100px' }}>
                  Tila
                </Th>

                <Th _id="tes_name" style={{ width: '95px' }}>
                  Ruokatauko
                </Th>

                <Th _id="employee_ids" style={{ width: '95px' }}>
                  Päiväraha
                </Th>
                <Th _id="buttons" style={{ width: '95px' }} className="s-hide-on-print">
                  Ateriatuki
                </Th>
                <Th _id="buttons" style={{ width: '150px' }} className="s-hide-on-print">
                  Kilometrikorvaus
                </Th>
                <Th _id="buttons" style={{ width: '200px' }} className="s-hide-on-print">
                  Lisätiedot
                </Th>
              </Tr>
            }
            tbody={employeeReports?.data.map((item) => {
              return (
                <Tr style={{ cursor: 'pointer' }} selected={selectedReportIds.includes(item._id)}>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    <Checkbox checked={selectedReportIds.includes(item._id)} />
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    <b> {item.id}</b>
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    <div style={{ width: '100%', marginRight: '1em' }}>
                      <div style={{ width: '100%', display: 'flex' }}>
                        <div style={{ flex: 1, justifyContent: 'flex-start' }}>
                          <b>{moment(item.start_time_iso_date).format('dddd').toUpperCase()}</b>
                        </div>
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <b>VIIKKO {moment(item.start_time_iso_date).week()}</b>
                        </div>
                      </div>
                      <div style={{ display: 'flex' }}>
                        <div style={{ flex: 1 }}>{moment(item.start_time_iso_date).format('DD.MM.YYYY')}</div>
                        <div style={{ flex: 1, justifyContent: 'flex-end', display: 'flex' }}>
                          {millisecondsToDuration(item.data.total_work_time)}
                        </div>
                      </div>
                      <div style={{ display: 'flex' }}>
                        <div style={{ flex: 1 }}>{moment(item.start_time_iso_date).fromNow()}</div>
                        <div style={{ flex: 1, justifyContent: 'flex-end', display: 'flex' }}>
                          <div>{moment(item.start_time_iso_date).format('HH:mm')}</div> {'  -  '}
                          <div>{moment(item.end_time_iso_date).format('HH:mm')}</div>
                        </div>
                      </div>
                    </div>
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    <div>
                      <div>{item.meta?.customer.name}</div>
                      <div>{item.meta?.customer.description}</div>
                      <div>{item.meta?.customer.business_identity_code}</div>
                    </div>
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    {item.meta?.customer.assignment?.name}
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    <div>
                      <div>
                        {item.meta?.employee.last_name} {item.meta?.employee.first_name}
                      </div>
                    </div>
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    <ReportStatusIndicator status={item.status} />
                  </Td>

                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    {item.data.lunch_break_duration ? item.data.lunch_break_duration + ' min' : ''}
                  </Td>

                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    {item.data.daily_allowance ? 'Kyllä' : 'Ei'}
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    {item.data.lunch_support ? 'Kyllä' : 'Ei'}
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    {item.data.driving_kilometers ? `${item.data.driving_kilometers} km` : ''}
                  </Td>
                  <Td
                    onClick={() => {
                      toggleSelectedReportIds(item)
                    }}
                  >
                    {item.data.additional_details}
                  </Td>
                </Tr>
              )
            })}
          />
        )}

        <div style={{ position: 'fixed', right: '1.5em', bottom: '1em', display: 'flex', zIndex: 100 }}>
          {selectedReportIds.length === 0 && (
            <>
              {employeeReports && employeeReports?.data?.length > 0 && (
                <Button
                  color="blue"
                  label={<i className="fas fa-file-excel"></i>}
                  onClick={() => {
                    if (employeeReports.data) {
                      downloadExcel({ employeeReports: employeeReports.data })
                    }
                  }}
                  rounded={true}
                  style={{ marginRight: '4px' }}
                />
              )}
              <Button color="green" label={<i className="fa-solid fa-plus"></i>} rounded={true} onClick={() => setVisibleModal('create-report')} />
            </>
          )}
          {selectedReportIds.length > 0 && (
            <>
              <Button
                color="blue"
                rounded={true}
                label={selectedReportIds.length}
                onClick={() => setSelectedReportIds([])}
                style={{ color: 'white' }}
              />
              {selectedReportIds.length === 1 && (
                <Button
                  color="blue"
                  rounded={true}
                  icon={<i className="fa-solid fa-pen-to-square"></i>}
                  onClick={() => {
                    const found = employeeReports?.data.find((item) => {
                      return item._id === selectedReportIds[0]
                    })
                    if (found) {
                      setEditableEmployeeReport(found)
                    }
                  }}
                  style={{ color: 'white', marginLeft: '4px' }}
                />
              )}
              <Button
                color="blue"
                rounded={true}
                icon={<i className="fa-solid fa-arrow-right"></i>}
                onClick={() => setVisibleModal('change-multiple-status')}
                style={{ color: 'white', marginLeft: '4px' }}
              />
              <Button
                color="red"
                rounded={true}
                icon={<i className="fa-solid fa-trash"></i>}
                onClick={() => setVisibleModal('delete-multiple')}
                style={{ marginLeft: '4px' }}
              />
            </>
          )}
        </div>
        <div style={{ height: '400px' }}></div>
      </Content>
    </>
  )
}

export default EmployeeHourReport
